import { buildAssistantHeaders, buildAssistantUrl } from "./assistantApi";

const throwIfAborted = (signal) => {
  if (!signal?.aborted) return;
  throw signal.reason || new DOMException("Audio playback was cancelled.", "AbortError");
};

/**
 * Strip markdown syntax so TTS reads spoken words, not literal symbols
 * (e.g. "**word**" would otherwise be read as "asterisk asterisk word asterisk asterisk").
 */
export const stripMarkdownForSpeech = (text) => {
  let plain = String(text || "");

  plain = plain.replace(/```[\s\S]*?```/g, " ");
  plain = plain.replace(/`([^`]+)`/g, "$1");
  plain = plain.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  plain = plain.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  plain = plain.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  plain = plain.replace(/^\s{0,3}>+\s?/gm, "");
  plain = plain.replace(/^\s*([-*_]\s*){3,}$/gm, " ");
  plain = plain.replace(/^\s*\d+[.)]\s+/gm, "");
  plain = plain.replace(/^\s*[-*+]\s+/gm, "");
  plain = plain.replace(/\*\*\*([^*]+)\*\*\*/g, "$1");
  plain = plain.replace(/\*\*([^*]+)\*\*/g, "$1");
  plain = plain.replace(/__([^_]+)__/g, "$1");
  plain = plain.replace(/\*([^*]+)\*/g, "$1");
  plain = plain.replace(/(?<!\w)_([^_]+)_(?!\w)/g, "$1");
  plain = plain.replace(/~~([^~]+)~~/g, "$1");
  plain = plain.replace(/^\s*\|.*\|\s*$/gm, " ");
  plain = plain.replace(/[*_~`#>|]/g, "");
  plain = plain.replace(/[ \t]+/g, " ");
  plain = plain.replace(/\n{2,}/g, ". ");
  plain = plain.replace(/\n/g, " ");

  return plain.replace(/\s+/g, " ").trim();
};

export const playAssistantSpeech = async (text, options = {}) => {
  const { signal, onStateChange } = options;
  const trimmedText = stripMarkdownForSpeech(text);
  if (!trimmedText) return;

  throwIfAborted(signal);
  onStateChange?.("loading");

  const response = await fetch(buildAssistantUrl("voice/synthesize"), {
    method: "POST",
    headers: buildAssistantHeaders(),
    credentials: "include",
    body: JSON.stringify({ text: trimmedText }),
    signal,
  });

  if (!response.ok) {
    throw new Error(response.statusText || "Voice synthesis failed");
  }

  const audioBlob = await response.blob();
  throwIfAborted(signal);

  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);
  let abortHandler = null;

  try {
    await new Promise((resolve) => {
      const cleanup = () => {
        audio.onplaying = null;
        audio.onended = null;
        audio.onerror = null;
        if (abortHandler) {
          signal?.removeEventListener("abort", abortHandler);
        }
      };

      abortHandler = () => {
        audio.pause();
        audio.currentTime = 0;
        cleanup();
        resolve();
      };

      signal?.addEventListener("abort", abortHandler, { once: true });
      audio.onplaying = () => onStateChange?.("playing");
      audio.onended = () => {
        cleanup();
        resolve();
      };
      audio.onerror = () => {
        cleanup();
        resolve();
      };

      audio.play().catch(() => {
        cleanup();
        resolve();
      });
    });
  } finally {
    audio.pause();
    URL.revokeObjectURL(audioUrl);
    onStateChange?.("idle");
  }
};
